import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild,
  Renderer2,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { AvailabilityDatesService } from '../../../core/services/availability-dates/availability-dates.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { bookingMaxAdults, bookingMaxChildren, bookingMaxRooms } from '../../../constants/booking-guests.constants';
import { modalVars } from '../../../constants/modal-vars';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import SimpleBar from 'simplebar';
import { IGuestsData } from '../../../core/interfaces/guests-data';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { BREAKPOINTS_WIDTH } from '@shared/common/services/viewport/constants/breakpoint.constant';

@Component({
  selector: 'app-guest-picker',
  templateUrl: './guest-picker.component.html',
  styleUrls: ['./guest-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GuestPickerComponent implements OnInit, OnChanges {
  showOverlay = false;
  guests: number;
  adultsTemp: number;
  childrenTemp: number;
  roomsTemp: number;
  bookingMaxAdults: number;
  bookingMaxRooms: number;
  bookingMaxChildren: number;
  isUpdateable = true;
  modalVars = modalVars;
  isUnselectedAge = false;
  showAgeDropdown = false;
  ageDropdownIndex: number;
  adults = 1;
  children = 0;
  rooms = 1;
  childrenAgesArr: number[] = [];
  childrenAgesArrTemp: number[] = [];
  valueType = {
    adults: 'adults',
    children: 'children',
    rooms: 'rooms'
  };
  simpleScrollBar: ElementRef;
  scrollbar: SimpleBar;
  isSelectionConfirmed = false;

  @Input() triggerElement: HTMLElement;
  @Input() guestPickerModalId: string = 'guest-picker';
  @Input() useAsInventory = false;
  @Input() maxGuests: number;
  @Input() hideRooms = false;
  @Input() showDropdown = false;
  @Input() isSearchWidget: boolean;
  @Input() parentShadowRoot: ShadowRoot;
  @Input() isFetching: boolean;
  @Input() ctaText: string;
  @Input() openOverlayOnMobile = false;
  @Input() openModalOnMobile = false;

  @Input() set adultsNum(adults: number) {
    this.adults = adults;
    this.adultsTemp = adults;
  }
  @Input() set roomsNum(rooms: number) {
    this.rooms = rooms;
    this.roomsTemp = rooms;
  }
  @Input() set childrenAges(childrenAges: number[] | number | string) {
    if (
      (!this.updateGuestWithoutRequest && childrenAges && childrenAges != '') ||
      (childrenAges == 0 && typeof childrenAges == 'number')
    ) {
      this.childrenAgesArr = childrenAges
        .toString()
        .split(',')
        .map((x) => +x);
      this.childrenAgesArrTemp = [...this.childrenAgesArr];
      this.children = this.childrenAgesArr.length;
      this.childrenTemp = this.children;
    }
  }
  @Input() classList: string;
  @Input() updateGuestWithoutRequest: boolean;
  @Input() isExtension: boolean;
  @Input() set shouldUpdateGuest(update: boolean) {
    this.isUpdateable = !update;
  }

  @Output() guestsData: EventEmitter<IGuestsData>;
  @Output() dropdownStatus: EventEmitter<boolean>;
  @Output() ageSelectionStatus: EventEmitter<boolean>;
  @Output() getUnitGroupAvailability: EventEmitter<boolean> = new EventEmitter();
  @Output() closeDesktopGuestPicker: EventEmitter<any> = new EventEmitter();
  @Output() guestChangeSideEffect: EventEmitter<{ adults: number; childrenAges: string | number[] }> =
    new EventEmitter();
  @Output() trackClickEvent: EventEmitter<string> = new EventEmitter();

  @ViewChild('simpleScrollBar', { static: false }) set simpleScrollBarElem(simpleScrollBar: ElementRef) {
    if (simpleScrollBar) {
      this.simpleScrollBar = simpleScrollBar;
    }
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get totalGuests(): number {
    return Number(this.adults) + Number(this.children);
  }

  get disabledIncrementerControl(): boolean {
    const tempTotalGuests = Number(this.adultsTemp) + Number(this.childrenTemp);
    const hasGuestOverflow = this.maxGuests && tempTotalGuests >= this.maxGuests;

    return hasGuestOverflow;
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  constructor(
    public messageService: MessageService,
    public viewportService: ViewportService,
    private readonly resizeService: ResizeService,
    private readonly elemRef: ElementRef,
    private readonly availabilityDatesService: AvailabilityDatesService,
    private readonly modalService: ModalService,
    public translateService: TranslateService,
    private readonly renderer: Renderer2,

    @Inject(DOCUMENT) private readonly document: any
  ) {
    this.guestsData = new EventEmitter();
    this.dropdownStatus = new EventEmitter();
    this.ageSelectionStatus = new EventEmitter();
    this.bookingMaxAdults = bookingMaxAdults;
    this.bookingMaxRooms = bookingMaxRooms;
    this.bookingMaxChildren = bookingMaxChildren;
    this.modalService.setRender(this.renderer);
  }

  ngOnInit() {
    this.adultsTemp = this.adults;
    this.childrenTemp = this.children;
    this.roomsTemp = this.rooms;

    setTimeout(() => {
      this.availabilityDatesService.guestsData.next({
        adults: this.adults,
        childrenAges: this.childrenAgesArr.toString(),
        rooms: this.rooms
      });
    });

    this.resizeService.resize().subscribe(() => {
      if (modalVars[this.guestPickerModalId] && !this.isMobile) {
        this.closeModal(this.guestPickerModalId, true);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.updateGuestWithoutRequest) {
      // Fix for: the component is showing old value for guestsTemp #BW-403
      this.adultsTemp = this.adults;
      this.childrenTemp = this.children;
      this.roomsTemp = this.rooms;
    }
  }

  /**
   * @description
   * Close Drop Down by click outside
   */
  @HostListener('document:click', ['$event'])
  clickOutsideDropDown(event) {
    const target = event.target as Node;

    if (
      this.triggerElement &&
      (this.triggerElement === target ||
        this.triggerElement.contains(target) ||
        this.elemRef.nativeElement.contains(target))
    )
      return;

    if (this.showDropdown && !this.isMobile && !this.elemRef.nativeElement.contains(event.composedPath()[0])) {
      if (
        !this.isSearchWidget ||
        (this.isSearchWidget &&
          (event.composedPath()[0].getAttribute('id') !== 'qa_search-button' ||
            (event.composedPath()[0].getAttribute('id') == 'qa_search-button' && !this.checkUnselectedAge())))
      ) {
        if (this.closeDesktopGuestPicker && this.closeDesktopGuestPicker.observers.length > 0) {
          this.closeDesktopGuestPicker.emit();
        } else {
          this.toggleDropdown();
        }
      }

      this.isUnselectedAge =
        this.isSearchWidget &&
        event.composedPath()[0].getAttribute('id') == 'qa_search-button' &&
        this.checkUnselectedAge();
    }
  }

  preventClick(event) {
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * @description
   * Shows and hides the dropdown
   */
  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
    this.dropdownStatus.emit(this.showDropdown);
    this.isSelectionConfirmed = false;

    const windowWidth = window.innerWidth;
    if (windowWidth < BREAKPOINTS_WIDTH.MOBILE) {
      // iOS scrolling issue fix - passing true to disable iOS scrolling when modal is opened
      this.modalService.handleIosScrolling(true);
      this.modalService.open(this.guestPickerModalId, true);
    }

    if (!this.showDropdown) {
      this.resetToPrevState();
    }
  }

  toggleAgeDropdown(index: number, event) {
    if (this.showAgeDropdown && this.ageDropdownIndex == index) {
      this.showAgeDropdown = false;
      this.ageDropdownIndex = null;
      this.unmountScrollbar();

      return;
    }

    this.showAgeDropdown = true;
    this.ageDropdownIndex = index;
    this.initScrollbar();

    if (event && event.type) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.scrollToSelectedCity();
  }

  scrollToSelectedCity(selector: string = '.widget__age-select-list-item--selected') {
    // Ensure that the simple scrollbar directive is loaded
    setTimeout(() => {
      if (!this.simpleScrollBar) {
        return;
      }

      const selectedAgeItem = this.simpleScrollBar.nativeElement.querySelector(selector) as HTMLElement;
      const simpleBarWrapper = this.simpleScrollBar.nativeElement.querySelector(
        '.simplebar-content-wrapper'
      ) as HTMLElement;

      if (!selectedAgeItem) {
        simpleBarWrapper.scrollTop = 0;
        return;
      }

      const { scrollTop: simpleBarWrapperTop, clientHeight: simpleBarWrapperHeight } = simpleBarWrapper;
      const { offsetTop: ageItemTop, offsetHeight: ageItemHeight } = selectedAgeItem;
      const simpleBarWrapperBottom = simpleBarWrapperTop + simpleBarWrapperHeight;
      const ageItemBottom = ageItemTop + ageItemHeight;

      // age item is inside the scroll
      if (ageItemTop >= simpleBarWrapperTop && ageItemBottom <= simpleBarWrapperBottom) {
        return;
      }

      // age item is outside the scroll, but above the current position
      if (ageItemTop < simpleBarWrapperTop) {
        // set the wrapper scroll position to be the same as age item top
        simpleBarWrapper.scrollTop = ageItemTop;
        return;
      }

      // ageItem is outside the scroll, but below the current position (and partially covered)
      if (ageItemBottom > simpleBarWrapperBottom) {
        // set the wrapper scroll position to be (vertical position of age inside the scroll - scroll height) + item height
        simpleBarWrapper.scrollTop = ageItemTop - simpleBarWrapperHeight + ageItemHeight;
        return;
      }
    }, 0);
  }

  initScrollbar() {
    if (!this.showAgeDropdown || !this.parentShadowRoot) {
      return;
    }

    setTimeout(() => {
      const scrollbarElem = this.parentShadowRoot?.querySelector('[data-simplebar]') as HTMLElement | undefined;

      if (scrollbarElem) {
        this.scrollbar = new SimpleBar(scrollbarElem);
      }
    });
  }

  unmountScrollbar() {
    setTimeout(() => {
      this.scrollbar?.unMount();
    });
  }

  closeModal(modalId: string, isIphoneModalFix?: boolean) {
    this.modalService.close(modalId, isIphoneModalFix);
    this.isSelectionConfirmed = false;

    this.resetToPrevState();

    // passing false to enable scrolling on iOS when modal is closed
    this.modalService.handleIosScrolling(false);
  }

  resetToPrevState() {
    this.isUnselectedAge = false;
    this.adultsTemp = this.adults;
    this.roomsTemp = this.rooms;
    this.childrenTemp = this.children;
    this.childrenAgesArrTemp = [...this.childrenAgesArr];
  }

  selectChildAge(index: number, age: number, event) {
    this.childrenAgesArrTemp[index] = age;
    this.showAgeDropdown = false;

    if (this.updateGuestWithoutRequest && !this.isMobile) {
      this.children = 0;
      this.childrenAgesArr = [];

      this.childrenAgesArrTemp.map((age) => {
        if (age !== null) {
          this.children++;
          this.childrenAgesArr.push(age);
        }
      });

      this.sendGuestsData();
      this.ageSelectionStatus.emit(this.checkUnselectedAge());
    }

    if (event && event.type) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.unmountScrollbar();
  }

  /**
   * @description
   * Increments the number of guests or rooms.
   */
  increment(type?: any): void {
    this.isUnselectedAge = false;

    if (type === this.valueType.adults) {
      if (this.adultsTemp < bookingMaxAdults && this.isUpdateable) {
        this.adultsTemp++;

        if (this.updateGuestWithoutRequest && !this.isMobile) {
          this.adults = this.adultsTemp;
          this.sendGuestsData();
        }

        if (!this.isSearchWidget) {
          this.trackClickEvent.emit('product_details_room_adult_increment_button');
        }
      }
    } else if (type === this.valueType.rooms) {
      if (this.roomsTemp < bookingMaxRooms && this.isUpdateable) {
        this.roomsTemp++;

        if (this.updateGuestWithoutRequest && !this.isMobile) {
          this.rooms = this.roomsTemp;
          this.sendGuestsData();
        }
        if (this.isSearchWidget) {
          this.trackClickEvent.emit('homepage_search_widget_rooms_increment_button');
        }
      }
    } else {
      if (this.childrenTemp < bookingMaxChildren && this.isUpdateable) {
        this.childrenTemp++;
        this.childrenAgesArrTemp[this.childrenTemp - 1] = null;
      }

      if (!this.isSearchWidget) {
        this.trackClickEvent.emit('product_details_room_child_increment_button');
      }

      if (this.updateGuestWithoutRequest && !this.isMobile) {
        this.ageSelectionStatus.emit(this.checkUnselectedAge());
      }
    }
  }

  /**
   * @description
   * Decrements the number of guests or rooms.
   */
  decrement(type?: any): void {
    this.isUnselectedAge = false;

    if (type === this.valueType.adults) {
      if (this.adultsTemp > 1 && this.isUpdateable) {
        this.adultsTemp--;

        if (this.updateGuestWithoutRequest && !this.isMobile) {
          this.adults = this.adultsTemp;
          this.sendGuestsData();
        }
      }
    } else if (type === this.valueType.rooms) {
      if (this.roomsTemp > 1 && this.isUpdateable) {
        this.roomsTemp--;

        if (this.updateGuestWithoutRequest && !this.isMobile) {
          this.rooms = this.roomsTemp;
          this.sendGuestsData();
        }
      }
    } else {
      if (this.childrenTemp >= 1 && this.isUpdateable) {
        this.childrenTemp--;

        if (this.updateGuestWithoutRequest && !this.isMobile) {
          this.adults = this.adultsTemp;
          this.childrenAgesArrTemp.pop();

          this.children = 0;
          this.childrenAgesArr = [];

          this.childrenAgesArrTemp.map((age) => {
            if (age !== null) {
              this.children++;
              this.childrenAgesArr.push(age);
            }
          });
          this.sendGuestsData();
          this.ageSelectionStatus.emit(this.checkUnselectedAge());
        } else {
          this.childrenAgesArrTemp.length = this.childrenTemp;
        }
      }
    }
  }

  publishAndFetchByGuests() {
    this.guestsData.emit({
      guests: this.guests,
      childrenAges: this.childrenAgesArr.toString(),
      children: this.children,
      adults: this.adults,
      rooms: this.rooms
    });
    this.availabilityDatesService.guestsData.next({
      adults: this.adults,
      childrenAges: this.childrenAgesArr.toString(),
      rooms: this.rooms
    });
  }

  /**
   * @description
   * Confirms the chosen selection
   */
  confirmSelection(): void {
    this.isSelectionConfirmed = true;

    this.isUnselectedAge = this.checkUnselectedAge();

    if (this.isUnselectedAge) {
      return;
    }

    const hasUpdates = !(
      this.adults === this.adultsTemp &&
      this.rooms === this.roomsTemp &&
      this.children === this.childrenTemp &&
      this.childrenAgesArr && [...this.childrenAgesArrTemp]
    );

    this.adults = this.adultsTemp;
    this.rooms = this.roomsTemp;
    this.children = this.childrenTemp;
    this.childrenAgesArr = [...this.childrenAgesArrTemp];

    if (this.useAsInventory) {
      if (hasUpdates) {
        this.guestChangeSideEffect.emit({
          adults: this.adults,
          childrenAges: [...this.childrenAgesArrTemp].toString()
        });
      }

      this.clearSelection();
      this.closeGuestPicker();
      return;
    }

    this.sendGuestsData();
    this.closeGuestPicker();
  }

  closeGuestPicker() {
    if (this.isMobile) {
      this.closeModal(this.guestPickerModalId, true);
    } else {
      this.toggleDropdown();
    }
  }

  checkUnselectedAge() {
    return this.childrenAgesArrTemp.length ? !this.childrenAgesArrTemp.every((age) => age !== null) : false;
  }

  clearSelection() {
    this.roomsTemp = 1;
    this.adultsTemp = 1;
    this.childrenTemp = 0;
    this.isUnselectedAge = false;
    this.isSelectionConfirmed = false;
    this.childrenAgesArrTemp = [];
  }

  sendGuestsData() {
    this.publishAndFetchByGuests();
  }

  trackByAge(index, item) {
    return item;
  }

  /**
   * @description
   * Opens the guest overlay
   */

  checkOpenOverlay() {
    const windowWidth = window.outerWidth;
    if ((this.openOverlayOnMobile || this.openModalOnMobile) && windowWidth < BREAKPOINTS_WIDTH.MOBILE) {
      this.showOverlay = this.openOverlayOnMobile;
      if (this.openModalOnMobile) {
        this.modalService.open(this.guestPickerModalId, true);
        return;
      }
      this.messageService.updateOverlayStatus(true, true);
    }
  }

  /**
   * @description
   * Closes the overlay
   */
  closeOverlay(): void {
    this.showOverlay = false;
    this.messageService.updateOverlayStatus(false, false);
  }
}
